// Preset-sourced responsive spacing system.
//
// PrimeNG preset tokens are static — they cannot express the breakpoint-
// responsive spacing scale (--spacing-medium-5 grows from 28px to 40px across
// sm/md/lg/xl). Instead of the former hand-written responsive-spacing.scss,
// each theme now OWNS its spacing map (values in px) and the app bootstrap
// emits it as a <style> tag with :root + @media blocks (applySpacingTheme).
// Both maps below carry the historical scale today, but a theme may diverge.

export type SpacingValues = {
    'spacing-huge-2'?: number;
    'spacing-huge-1'?: number;
    'spacing-medium-5'?: number;
    'spacing-medium-4'?: number;
    'spacing-medium-3'?: number;
    'spacing-medium-2'?: number;
    'spacing-medium-1'?: number;
    'spacing-small-3'?: number;
    'spacing-small-2'?: number;
    'spacing-small-1'?: number;
    'main-border'?: number;
};

export type SpacingScale = {
    /** base values (mobile-first, below `sm`) */
    base: SpacingValues;
    /** cumulative overrides applied at each breakpoint (min-width) */
    sm?: SpacingValues;
    md?: SpacingValues;
    lg?: SpacingValues;
    xl?: SpacingValues;
};

// Breakpoints — kept in sync with $breakpoints in theme/styles/base/_mixins.scss
const BREAKPOINTS: Record<keyof Omit<SpacingScale, 'base'>, number> = {
    sm: 576,
    md: 768,
    lg: 992,
    xl: 1200
};

/** Cleverly (current) theme spacing — ported 1:1 from responsive-spacing.scss. */
export const cleverlySpacing: SpacingScale = {
    base: {
        'spacing-huge-2': 40,
        'spacing-huge-1': 28,
        'spacing-medium-5': 28,
        'spacing-medium-4': 24,
        'spacing-medium-3': 20,
        'spacing-medium-2': 12,
        'spacing-medium-1': 8,
        'spacing-small-3': 8,
        'spacing-small-2': 4,
        'spacing-small-1': 2,
        'main-border': 20
    },
    sm: { 'spacing-medium-5': 32, 'spacing-medium-4': 28, 'main-border': 40 },
    md: { 'spacing-huge-2': 48, 'spacing-huge-1': 32, 'main-border': 32 },
    lg: {
        'spacing-huge-2': 80,
        'spacing-huge-1': 56,
        'spacing-medium-5': 32,
        'spacing-medium-4': 32,
        'spacing-medium-3': 24,
        'spacing-medium-2': 16,
        'spacing-medium-1': 12,
        'main-border': 32
    },
    xl: { 'spacing-huge-1': 64, 'spacing-medium-5': 40, 'main-border': 56 }
};

/** Legacy (old cleverly) theme spacing — identical to the historical scale. */
export const legacySpacing: SpacingScale = cleverlySpacing;

/** px → rem, matching the old px() SCSS helper (16px = 1rem). */
function toRem(px: number): string {
    return `${px / 16}rem`;
}

function declarations(values: SpacingValues): string {
    return Object.entries(values)
        .map(([name, px]) => `--${name}:${toRem(px as number)};`)
        .join('');
}

/** Build the CSS text: :root base + cumulative @media overrides per breakpoint. */
export function buildSpacingCss(scale: SpacingScale): string {
    let css = `:root{${declarations(scale.base)}}`;
    let cumulative: SpacingValues = { ...scale.base };
    (Object.keys(BREAKPOINTS) as Array<keyof typeof BREAKPOINTS>).forEach(bp => {
        const override = scale[bp];
        if (!override || Object.keys(override).length === 0) return;
        cumulative = { ...cumulative, ...override };
        css += `@media (min-width:${BREAKPOINTS[bp]}px){:root{${declarations(cumulative)}}}`;
    });
    return css;
}

const STYLE_ID = 'cl-spacing-theme';

/**
 * Emit the theme's responsive spacing scale into the document as a style tag
 * (idempotent — replaces any previous one). Call once at app bootstrap,
 * BEFORE Angular renders, so the --spacing-* and --main-border variables are
 * live from the first paint (same timing the old SCSS :root block had).
 */
export function applySpacingTheme(scale: SpacingScale): void {
    if (typeof document === 'undefined') return; // not running in browser
    try {
        let style = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
        if (!style) {
            style = document.createElement('style');
            style.id = STYLE_ID;
            document.head.appendChild(style);
        }
        style.textContent = buildSpacingCss(scale);
    } catch (e) {
        // swallow to avoid breaking bootstrap; log for debugging
        // eslint-disable-next-line no-console
        console.error('applySpacingTheme failed', e);
    }
}
